


/**
PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 *  Copyright © 2024 Pearson Education, Inc.
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
* */

/**
 * SessionModal component
 *
 * @file SessionModal.jsx
 * @author Ram Suresh Yejju
 * @since 1.0.0
 */

import React from 'react';
import PropTypes from 'prop-types';
import ReactHtmlParser from 'react-html-parser';
import { Dialog } from '@mui/material';
import CloseModalIcon from '../../icons/CloseModalIcon';
import { EREADER } from '../../constants';
import {
  DialogPaper,
  DialogTitleContainer,
  DialogTitleText,
  DialogCloseButton,
  DialogContentContainer,
  DialogContentText,
  DialogActionsContainer,
  ActionButton,
  SignOutButton,
  getBackdropSlotProps
} from './styles/SessionModalStyles';

const SessionModal = ({
  open,
  data,
  location
}) => {
  const {
    title,
    description,
    actions,
    onClose
  } = data;

  const getActionButton = (isSecondary) => (isSecondary ? SignOutButton : ActionButton);

  const handleCloser = (event, reason) => {
    if (reason !== 'escapeKeyDown' && reason !== 'backdropClick') {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      role="dialog"
      aria-modal="true"
      disableEscapeKeyDown
      onClose={handleCloser}
      maxWidth={false}
      slotProps={{
        paper: {
          component: DialogPaper,
          tabIndex: 0,
          isEreader: location === EREADER
        },
        backdrop: getBackdropSlotProps(location)
      }}
    >
      <DialogTitleContainer disableTypography>
        {
          title && (<DialogTitleText>{title}</DialogTitleText>)
        }
        <DialogCloseButton onClick={handleCloser}><CloseModalIcon /></DialogCloseButton>
      </DialogTitleContainer>
      <DialogContentContainer>
        <DialogContentText>
          {ReactHtmlParser(description)}
        </DialogContentText>
      </DialogContentContainer>
      <DialogActionsContainer>
        {
          actions && actions.length > 0 && actions.map((action, index) => {
            const ActionBtn = getActionButton(action.isSecondary);

            return (
              <ActionBtn onClick={action.callback} key={index}>
                {action.text}
              </ActionBtn>
            );
          })
        }
      </DialogActionsContainer>
    </Dialog>
  );
};

SessionModal.defaultProps = {
  open: false
};

SessionModal.propTypes = {
  open: PropTypes.bool,
  data: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    actions: PropTypes.array,
    onClose: PropTypes.func
  }).isRequired,
  location: PropTypes.string.isRequired
};

export default SessionModal;
